/**
 * MiPerfil — pantalla de perfil del usuario.
 *
 * Separa a propósito las dos cosas que se suelen mezclar:
 *
 *   · Lo que es de ESTA aplicación → aspecto (skin + paleta) e idioma. Se
 *     aplica al instante y se guarda en la preferencia del usuario.
 *   · La IDENTIDAD (nombre, correo, contraseña, doble factor) → vive en el
 *     IdP. Aquí solo se enlaza a su pantalla de perfil.
 *
 * El enlace al IdP sale del IdP de referencia de la instancia (`/api/version`
 * → `idp_url`), no de un `localhost` embebido en el build: funciona igual en
 * desarrollo y en producción.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowTopRightOnSquareIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import AppearanceSelector from '../components/AppearanceSelector';
import api from '../services/api';
import idpService from '../services/idp';
import { changeLanguage, fetchRemoteLanguages } from '../i18n/config';
import { BUILTIN_LANGUAGES, mergeLanguages } from '../i18n/languages';
import { getUserDisplayName, getUserInitials } from '../utils/userDisplay';
import './MiPerfil.css';

function MiPerfil({ user, companyId, companyTheme, onLogout }) {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [languages, setLanguages] = useState(BUILTIN_LANGUAGES);
  const [languageError, setLanguageError] = useState('');
  const [idpProfileUrl, setIdpProfileUrl] = useState(() => idpService.getProfileUrl());
  const [idpResolved, setIdpResolved] = useState(false);

  const displayName = useMemo(() => getUserDisplayName(user), [user]);
  const initials = useMemo(() => getUserInitials(user), [user]);
  const companyDefault = companyTheme?.company || null;

  // El selector se alimenta de los idiomas del build + los que el superadmin
  // haya añadido desde el panel de plataforma.
  useEffect(() => {
    let cancelled = false;
    fetchRemoteLanguages().then((remote) => {
      if (!cancelled) setLanguages(mergeLanguages(remote));
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  // Si el build no traía la URL del IdP, se pide al backend cuál es el suyo.
  useEffect(() => {
    let cancelled = false;
    idpService.resolveProfileUrl()
      .then((url) => {
        if (cancelled || !url) return;
        setIdpProfileUrl(url);
        setIdpResolved(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const idpHost = useMemo(() => {
    try {
      return new URL(idpProfileUrl).host;
    } catch (e) {
      return idpProfileUrl;
    }
  }, [idpProfileUrl]);

  const handleLanguageChange = async (event) => {
    const newLang = event.target.value;
    setLanguage(newLang);
    setLanguageError('');
    // Primero se aplica en esta pestaña (y se descarga el catálogo si es un
    // idioma nuevo); después se guarda en el perfil para el resto de equipos.
    try {
      await changeLanguage(newLang, { persist: true });
      await api.saveLanguagePreference(newLang);
    } catch {
      setLanguageError(
        t(
          'profile.language_error',
          'El idioma se ha aplicado en este navegador, pero no se ha podido guardar en tu perfil.'
        )
      );
    }
  };

  return (
    <div className="perfil-page">
      <header className="perfil-hero">
        <span className="perfil-avatar" aria-hidden="true">{initials}</span>
        <div className="perfil-hero-text">
          <h1 className="perfil-name">{displayName}</h1>
          {user?.email && <p className="perfil-email">{user.email}</p>}
          <p className="perfil-hero-hint">
            {t('profile.signed_in_with', 'Sesión iniciada con el IdP de Adder')}
          </p>
        </div>
      </header>

      <section className="perfil-card">
        <div className="perfil-card-head">
          <h2 className="perfil-card-title">{t('profile.appearance_title', 'Aspecto de la aplicación')}</h2>
          <p className="perfil-card-subtitle">
            {t(
              'profile.appearance_hint',
              'Elige cómo se ve el CMDB en este navegador. Se aplica al momento y se guarda en tu preferencia.'
            )}
          </p>
        </div>
        <AppearanceSelector
          companyId={companyId}
          companyDefault={companyDefault}
          serverTheme={companyTheme}
        />

        <div className="perfil-field">
          <label className="perfil-label" htmlFor="perfil-language">
            {t('profile.language', 'Idioma')}
          </label>
          <select
            id="perfil-language"
            className="form-select perfil-select"
            value={language}
            onChange={handleLanguageChange}
          >
            {languages.map((item) => (
              <option key={item.code} value={item.code}>
                {item.nativeName || item.name}
              </option>
            ))}
          </select>
          <p className="perfil-hint">
            {t(
              'profile.language_hint',
              'Se guarda en tu perfil y se aplica en todos los equipos desde los que entres.'
            )}
          </p>
          {languageError && <p className="perfil-hint perfil-hint--error">{languageError}</p>}
        </div>
      </section>

      <section className="perfil-card">
        <div className="perfil-card-head">
          <h2 className="perfil-card-title">{t('profile.identity_title', 'Mi identidad')}</h2>
          <p className="perfil-card-subtitle">
            {t(
              'profile.identity_hint',
              'Tu nombre, correo, contraseña y doble factor se gestionan en el IdP, que es la fuente de verdad para todas las aplicaciones de Adder.'
            )}
          </p>
        </div>

        <div className="perfil-identity-row">
          <div className="perfil-identity-data">
            <span className="perfil-label">{t('profile.name', 'Nombre')}</span>
            <span className="perfil-value">{displayName}</span>
            {user?.email && (
              <>
                <span className="perfil-label perfil-label--spaced">{t('profile.email', 'Correo')}</span>
                <span className="perfil-value">{user.email}</span>
              </>
            )}
          </div>

          <a
            className={`perfil-idp-btn ${idpResolved ? '' : 'is-pending'}`}
            href={idpProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!idpResolved}
            onClick={(event) => {
              // Hasta que el backend confirma cuál es el IdP, el href es solo
              // una suposición (host actual + 5001): no dejamos que se abra.
              if (!idpResolved) event.preventDefault();
            }}
            title={t('profile.manage_identity_hint', 'Abre el perfil del IdP en una pestaña nueva')}
          >
            <ArrowTopRightOnSquareIcon className="perfil-idp-icon" />
            <span>{t('profile.manage_identity', 'Gestionar mi identidad en el IdP')}</span>
          </a>
        </div>

        <p className="perfil-idp-host">
          {t('profile.idp_reference', 'IdP de referencia')}: <code>{idpHost}</code>
        </p>

        <div className="perfil-actions">
          <button type="button" className="perfil-logout-btn" onClick={onLogout}>
            <ArrowRightOnRectangleIcon className="perfil-logout-icon" />
            <span>{t('action.logout', 'Cerrar sesión')}</span>
          </button>
        </div>
      </section>
    </div>
  );
}

export default MiPerfil;
